import { useState } from 'react'
import { createPortal } from 'react-dom'
import { HelpCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation } from 'react-query'
import { feedbackAPI } from '../lib/api'
import toast from 'react-hot-toast'

export function KnowledgeGapButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [context, setContext] = useState('')
  const [priority, setPriority] = useState(3)

  const submitGap = useMutation(
    () => feedbackAPI.reportKnowledgeGap(question, context, priority),
    {
      onSuccess: () => {
        toast.success('Die fehlende Information wurde an das Management gemeldet')
        setIsOpen(false)
        setQuestion('')
        setContext('')
        setPriority(3)
      },
      onError: () => {
        toast.error('Fehler beim Melden der fehlenden Information')
      }
    }
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (question.trim()) {
      submitGap.mutate()
    }
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-smooth"
        title="Fehlende Informationen ans Management melden"
      >
        <HelpCircle className="w-4 h-4" />
        <span className="hidden md:inline">Fehlende Informationen</span>
      </button>

      {isOpen && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                style={{ zIndex: 99999 }}
              />

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                className="fixed top-20 left-1/2 transform -translate-x-1/2 max-w-md w-full px-4"
                style={{ zIndex: 100000 }}
              >
              <div className="bg-gray-900 rounded-xl p-6 shadow-2xl border border-white/20">
                <h2 className="text-xl font-bold mb-4">
                  Fehlende Informationen melden
                </h2>
                <p className="text-gray-400 text-sm mb-6">
                  Wenn Sie eine Information nicht finden können, leiten wir diese Meldung gerne an das Management weiter.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Ihre Frage *
                    </label>
                    <textarea
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="Was möchten Sie wissen?"
                      required
                      rows={3}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Kontext (optional)
                    </label>
                    <textarea
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      placeholder="Zusätzliche Informationen..."
                      rows={2}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Priorität
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      className="input w-full"
                    >
                      <option value={1}>Sehr hoch</option>
                      <option value={2}>Hoch</option>
                      <option value={3}>Normal</option>
                      <option value={4}>Niedrig</option>
                      <option value={5}>Sehr niedrig</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="flex-1 btn btn-secondary"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={submitGap.isLoading || !question.trim()}
                      className="flex-1 btn btn-primary"
                    >
                      {submitGap.isLoading ? 'Sende...' : 'Absenden'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}